/**
 * Catálogo de tipos de entidade da Central Unificada de Lembretes
 *
 * Fonte única de verdade (em código) sobre quais módulos aparecem
 * na Central de Lembretes (/tenant/reminders) e como cada um deve
 * ser renderizado pelo componente genérico <ReminderRulesManager />.
 *
 * Para adicionar um novo módulo (ex: contratos, CRM, tarefas):
 *   1. Adicionar valor ao enum reminder_entity_type (migration).
 *   2. Criar triggers SQL (gerar/atualizar reminder_queue).
 *   3. Estender o switch do reminder-worker-pgmq + resolve_reminder_template_vars.
 *   4. Adicionar uma entrada neste arquivo.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-02 v1.0.0 - Catálogo inicial (scheduling + cobranca)
 * RELEASE: 2026-10-02 v1.1.0 - Entrada 'vistoria' (lembretes por status da vistoria)
 */

import { Bell, CalendarClock, ClipboardCheck, Receipt, type LucideIcon } from 'lucide-react';
import type { ReminderEntityType } from '@/hooks/useReminderRules';

/** Definição de um tipo de entidade que suporta lembretes */
export interface ReminderEntityDefinition {
  /** Slug usado na coluna entity_type da reminder_rules / reminder_queue */
  entityType: ReminderEntityType;
  /** Label curto exibido na tab da Central */
  label: string;
  /** Texto auxiliar exibido no header do card de regras */
  description: string;
  /** Ícone Lucide para a tab */
  icon: LucideIcon;
  /** Permite offsets positivos (envio APÓS o evento — ex: cobrança vencida) */
  allowPositiveOffsets: boolean;
  /** Limite máximo de regras por tenant */
  maxRules: number;
  /** Chave usada pelo usePermissions (view/create/edit/delete) */
  permissionKey: string;
  /** Rota legada (página antiga) — usada como "atalho" no menu/Central */
  shortcutRoute?: string;
  /**
   * Flag para esconder o módulo da Central sem precisar remover do código.
   * Útil para módulos em desenvolvimento ou com rollout controlado.
   */
  isEnabled: boolean;
}

/**
 * Catálogo inicial.
 * Espelha exatamente os parâmetros usados hoje em:
 *  - FinancialBillingReminders.tsx (cobranca)
 *  - pages/tenant/Scheduling.tsx (scheduling)
 */
export const REMINDER_ENTITY_CATALOG: ReminderEntityDefinition[] = [
  {
    entityType: 'scheduling',
    label: 'Agendamento',
    description:
      'Lembretes automáticos enviados antes de eventos agendados. Aplicável a todos os tipos de evento.',
    icon: CalendarClock,
    allowPositiveOffsets: false,
    maxRules: 20,
    // Sem permissionKey específica até hoje — mantém liberado (igual a Scheduling.tsx)
    permissionKey: 'scheduling',
    shortcutRoute: '/tenant/scheduling',
    isEnabled: true,
  },
  {
    entityType: 'cobranca',
    label: 'Cobrança',
    description:
      'Lembretes antes do vencimento e notificações de atraso de cobranças financeiras.',
    icon: Receipt,
    allowPositiveOffsets: true,
    maxRules: 20,
    permissionKey: 'financial_billing_reminders',
    shortcutRoute: '/tenant/financial/billing-reminders',
    isEnabled: true,
  },
  {
    entityType: 'vistoria',
    label: 'Vistorias',
    description:
      'Lembretes por status da vistoria para o responsável (usuário, fila ou proprietário), com repetição opcional.',
    icon: ClipboardCheck,
    allowPositiveOffsets: true,
    maxRules: 30,
    permissionKey: 'vehicles_inspections',
    shortcutRoute: '/tenant/vehicles/inspections/kanban',
    isEnabled: true,
  },
];

/** Ícone padrão exibido no header da Central */
export const REMINDERS_CENTER_ICON = Bell;

/** Retorna apenas os módulos habilitados (isEnabled === true) */
export function getEnabledReminderEntities(): ReminderEntityDefinition[] {
  return REMINDER_ENTITY_CATALOG.filter((e) => e.isEnabled);
}
